import React, { useCallback, useEffect, useRef, useState } from 'react';
import { config } from '../../config';
import useGameStore from '../../stores/gameStore';
import useUIStore from '../../stores/uiStore';
import apiService from '../../services/api';
import Button from './Button';

export interface SlowResponseOverlayProps {
  /** When true, the overlay is visible. */
  isOpen: boolean;
}

const OVERLAY_TITLE_ID = 'slow-response-overlay-title';
const OVERLAY_DESC_ID = 'slow-response-overlay-desc';

/** Result of backend health check for the overlay. */
type BackendHealth = 'checking' | 'up' | 'down';

/** Result of session/WebSocket validity check (current-scene API). */
type SessionHealth = 'skip' | 'checking' | 'ok' | 'error';

/** How often to recheck backend and session health while the overlay is open (ms). */
const RECHECK_INTERVAL_MS = 5000;

const checkBackendHealth = async (backendUrl: string): Promise<BackendHealth> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 3000);
  const healthUrl = `${backendUrl}/health`;
  try {
    const response = await fetch(healthUrl, { method: 'GET', signal: controller.signal });
    clearTimeout(timeoutId);
    if (!response.ok) return 'down';
    const data = await response.json();
    const isHealthy = data.status === 'OK' || data.status === 'healthy';
    return isHealthy ? 'up' : 'down';
  } catch {
    clearTimeout(timeoutId);
    return 'down';
  }
};

/** Check if the current game session is still valid (e.g. not stale after server restart). Only run when backend is up. */
const checkSessionHealth = async (): Promise<SessionHealth> => {
  const { currentSession, currentPlayer } = useGameStore.getState();
  const sessionId = currentSession?.id;
  const playerId = currentPlayer?.id;
  if (!sessionId || !playerId) return 'skip';
  try {
    await apiService.getCurrentScene(sessionId, playerId);
    return 'ok';
  } catch {
    return 'error';
  }
};

/**
 * Overlay shown when a response is taking longer than expected.
 * Performs a backend health check and shows status when the server is up.
 */
const SlowResponseOverlay: React.FC<SlowResponseOverlayProps> = ({ isOpen }) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [backendHealth, setBackendHealth] = useState<BackendHealth>('checking');
  const [sessionHealth, setSessionHealth] = useState<SessionHealth>('skip');
  const [returnToSceneLoading, setReturnToSceneLoading] = useState(false);
  const isWebSocketConnected = useGameStore((state) => state.isConnected);
  const currentSession = useGameStore((state) => state.currentSession);
  const currentPlayer = useGameStore((state) => state.currentPlayer);
  const loadCurrentScene = useGameStore((state) => state.loadCurrentScene);
  const clearActionPending = useUIStore((state) => state.clearActionPending);

  const canReturnToScene = !!(currentSession?.id && currentPlayer?.id);

  const handleReturnToScene = useCallback(async () => {
    if (!canReturnToScene || returnToSceneLoading) return;
    setReturnToSceneLoading(true);
    try {
      await loadCurrentScene();
      clearActionPending();
    } catch (err) {
      console.error('[SlowResponseOverlay] Return to scene failed:', err);
      setReturnToSceneLoading(false);
    }
  }, [canReturnToScene, returnToSceneLoading, loadCurrentScene, clearActionPending]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      dialogRef.current?.focus();
      setBackendHealth('checking');
      setSessionHealth('skip');

      const runChecks = async () => {
        const backend = await checkBackendHealth(config.apiBaseUrl);
        setBackendHealth(backend);
        if (backend !== 'up') {
          setSessionHealth('skip');
          return;
        }
        const hasSession = !!(
          useGameStore.getState().currentSession?.id &&
          useGameStore.getState().currentPlayer?.id
        );
        if (!hasSession) {
          setSessionHealth('skip');
          return;
        }
        setSessionHealth('checking');
        const session = await checkSessionHealth();
        setSessionHealth(session);
      };

      runChecks();
      const intervalId = window.setInterval(runChecks, RECHECK_INTERVAL_MS);
      return () => {
        window.clearInterval(intervalId);
        document.body.style.overflow = 'unset';
      };
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9998,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={OVERLAY_TITLE_ID}
      aria-describedby={OVERLAY_DESC_ID}
    >
      {/* Backdrop - no click-to-close; overlay stays until response or state clears */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
        }}
        aria-hidden="true"
      />

      {/* Card */}
      <div
        ref={dialogRef}
        tabIndex={-1}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '28rem',
          background: 'rgba(255, 255, 255, 0.12)',
          backdropFilter: 'blur(12px) saturate(150%)',
          WebkitBackdropFilter: 'blur(12px) saturate(150%)',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '0.75rem',
          padding: '1.5rem',
        }}
      >
        <h2
          id={OVERLAY_TITLE_ID}
          style={{
            margin: 0,
            fontSize: '1.25rem',
            fontWeight: 600,
            color: 'rgba(255, 255, 255, 0.95)',
            marginBottom: '0.75rem',
          }}
        >
          This is taking longer than expected...
        </h2>
        <p
          id={OVERLAY_DESC_ID}
          style={{
            margin: 0,
            fontSize: '0.9375rem',
            lineHeight: 1.5,
            color: 'rgba(255, 255, 255, 0.85)',
            marginBottom: '1.25rem',
          }}
        >
          {backendHealth === 'up' && 'The backend server is up and running.'}
          {backendHealth === 'checking' && 'Checking backend server...'}
          {backendHealth === 'down' && '\u00A0'}
          <br />
          {isWebSocketConnected
            ? 'WebSocket: connected.'
            : 'WebSocket: disconnected.'}
          {sessionHealth === 'error' && (
            <>
              <br />
              Session may be invalid (e.g. after server restart). Try refreshing the page.
            </>
          )}
          {sessionHealth === 'ok' && (
            <>
              <br />
              Session: OK.
            </>
          )}
          {sessionHealth === 'checking' && (
            <>
              <br />
              Checking session...
            </>
          )}
        </p>
        {canReturnToScene && (
          <Button
            onClick={handleReturnToScene}
            disabled={returnToSceneLoading}
            loading={returnToSceneLoading}
            variant="glass-primary"
            className="w-full"
          >
            Return to scene
          </Button>
        )}
      </div>
    </div>
  );
};

export default SlowResponseOverlay;
